/**
 * Checkout step 3 — who receives the meals, and where.
 *
 * Split out of Donor Information, which had grown into a form you had to
 * scroll through twice: the donor's own details, then the city, then the
 * recipient, then the consents. These are a different question from "who is
 * giving", and the city belongs with them — it decides where the meals are
 * distributed, not where the donor lives.
 */

import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { Button } from '../../components/common/Button';
import { RecipientSection } from '../../components/donation/RecipientSection';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { SelectField } from '../../components/forms/SelectField';
import { space } from '../../theme';
import { useAppConfig } from '../../core/api/hooks';
import { useDonationStore } from '../donation/donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export function RecipientInformationScreen({
  navigation,
}: DonorScreenProps<'RecipientInformation'>) {
  const t = useT();
  const pick = useLocalized();
  const { data: config } = useAppConfig();

  const donor = useDonationStore((s) => s.donor);
  const setDonor = useDonationStore((s) => s.setDonor);
  const recipient = useDonationStore((s) => s.recipient);
  const setRecipient = useDonationStore((s) => s.setRecipient);
  const requiresEvidence = useDonationStore((s) => s.requiresDistributionEvidence);
  const setRequiresEvidence = useDonationStore((s) => s.setRequiresEvidence);

  // Doorstep delivery is the only mode with required fields, and they are only
  // required once it is chosen. The server re-checks all of this.
  const errors: Record<string, string> = {};
  if (recipient.fulfilmentType === 'DOORSTEP_DELIVERY') {
    if (!recipient.address?.addressLine.trim()) {
      errors.addressLine = 'Add the delivery address.';
    }
    const city = recipient.address?.city.trim() ?? '';
    const served = config?.delivery.cities ?? [];
    if (!city) {
      errors.city = 'Add the city.';
    } else if (served.length && !served.some((c) => c.toLowerCase() === city.toLowerCase())) {
      errors.city = `We deliver to ${served.join(' or ')} only.`;
    }
    if (!recipient.recipientContact?.trim()) {
      errors.recipientContact = 'Someone has to receive the meals.';
    }
  }

  const distributionCities = config?.cities ?? [];
  const cityMissing = Boolean(config?.requireCity) && !donor?.cityId;

  // With one city there is nothing to choose, so it is chosen — a donor
  // should not be stopped by a required field that has a single answer.
  useEffect(() => {
    const only = distributionCities.length === 1 ? distributionCities[0] : undefined;
    if (only && donor?.cityId !== only.id) {
      setDonor({ ...(donor ?? EMPTY_CITY_DONOR), cityId: only.id, cityName: null });
    }
  }, [distributionCities, donor, setDonor]);
  const ready = Object.keys(errors).length === 0 && !cityMissing;

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-recipient-information"
      subHeader={<CheckoutHeader current={2} title={t('Recipient Information')} subtitle={t('Where the meals go, and who receives them')} />}
      pinnedFooter={
        <Button
          label={t('Proceed to Payment')}
          onPress={() => navigation.navigate('Payment')}
          disabled={!ready}
          testID="cta-recipient-continue"
        />
      }
    >


      <View style={styles.body}>
        {/* The charity distributes in one city, so this is shown rather than
            asked: a dropdown of one implies a choice that does not exist. It
            stays a field because the answer is part of the donation, and it
            becomes a real choice again the day a second city opens. */}
        {config?.requireCity ? (
          <SelectField
            label={t('Which city should the meals go to?')}
            required
            disabled={distributionCities.length <= 1}
            placeholder={t('Select a city')}
            value={donor?.cityId ?? null}
            options={distributionCities.map((city) => ({
              value: city.id,
              label: pick(city.name, city.nameUr),
            }))}
            onChange={(value) => setDonor({ ...(donor ?? EMPTY_CITY_DONOR), cityId: value })}
          />
        ) : null}

        <RecipientSection
          value={recipient}
          onChange={setRecipient}
          delivery={config?.delivery}
          requiresEvidence={requiresEvidence}
          onRequiresEvidenceChange={setRequiresEvidence}
          errors={errors}
        />
      </View>
    </ScreenShell>
  );
}

/** Only reachable if the donor step were skipped; the city still has to land. */
const EMPTY_CITY_DONOR = {
  fullName: '',
  mobileNumber: '',
  email: '',
  cityId: null,
  cityName: null,
  donorCity: null,
  anonymous: false,
  receiveUpdates: false,
  shareDistributionPhotos: true,
};

const styles = StyleSheet.create({
  body: {
    gap: space.xl,
  },
});
